import Phaser from "phaser";
import { GAME_DURATION } from "../constants";
import { RHYTHM_REWARD_CONFIG } from "../rhythmRewardConfig";
import type { PickupData } from "../types";

export type RhythmJudgementId = "perfect" | "good" | "miss" | "none";

export interface RhythmJudgement {
  id: RhythmJudgementId;
  label: string;
  delta: number;
  combo: number;
  multiplier: number;
  scoreValue: number;
  comboBreak: boolean;
}

export class RhythmRewardSystem {
  private combo = 0;
  private multiplier = 1;
  private bestCombo = 0;
  private perfectPickups = 0;
  private goodPickups = 0;
  private missedPickups = 0;
  private floatingTexts = new Set<Phaser.GameObjects.Text>();

  constructor(private scene: Phaser.Scene, private layer: Phaser.GameObjects.Layer) {}

  reset() {
    this.combo = 0;
    this.multiplier = 1;
    this.bestCombo = 0;
    this.perfectPickups = 0;
    this.goodPickups = 0;
    this.missedPickups = 0;
    this.floatingTexts.forEach((text) => text.destroy());
    this.floatingTexts.clear();
  }

  updateMagnet(dt: number, pickups: PickupData[], wolf: { x: number; y: number }) {
    if (!RHYTHM_REWARD_CONFIG.magnet.enabled) return;

    pickups.forEach((pickup) => {
      if (!pickup.targetCounterTime || pickup.collected) return;
      const radius = this.isStrongPickup(pickup) ? RHYTHM_REWARD_CONFIG.magnet.strongRadius : RHYTHM_REWARD_CONFIG.magnet.baseRadius;
      const dx = wolf.x - pickup.x;
      const dy = wolf.y - pickup.y;
      const distance = Math.hypot(dx, dy);
      if (distance > radius || distance < RHYTHM_REWARD_CONFIG.magnet.minDistance) return;

      const pull = (1 - distance / radius) * RHYTHM_REWARD_CONFIG.magnet.pullStrength;
      const maxStep = RHYTHM_REWARD_CONFIG.magnet.maxPullPxPerSecond * dt;
      const step = Math.min(maxStep, pull * dt * 60);
      pickup.x += (dx / distance) * step;
      pickup.y += (dy / distance) * step;
    });
  }

  judgePickup(pickup: PickupData, songElapsedTime: number, wolf: { x: number; y: number }) {
    const baseScore = pickup.scoreValue ?? 1;
    if (pickup.targetCounterTime === undefined) {
      return { id: "none", label: "", delta: 0, combo: this.combo, multiplier: 1, scoreValue: baseScore, comboBreak: false } satisfies RhythmJudgement;
    }

    const actualCounterTime = GAME_DURATION - songElapsedTime;
    const delta = actualCounterTime - pickup.targetCounterTime;
    const absDelta = Math.abs(delta);
    const id = this.getJudgementId(absDelta);
    const comboBreak = id === "miss" && this.combo > 0;

    if (id === "perfect" || id === "good") this.combo += 1;
    else if (RHYTHM_REWARD_CONFIG.combo.resetOnMiss) this.combo = 0;
    if (id === "perfect") this.perfectPickups += 1;
    else if (id === "good") this.goodPickups += 1;
    else if (id === "miss") this.missedPickups += 1;
    this.bestCombo = Math.max(this.bestCombo, this.combo);

    this.multiplier = id === "miss" ? 1 : Math.min(RHYTHM_REWARD_CONFIG.combo.maxMultiplier, 1 + Math.max(0, this.combo - 1) * RHYTHM_REWARD_CONFIG.combo.multiplierStep);
    const label = this.getLabel(id);
    const judgement = {
      id,
      label,
      delta,
      combo: this.combo,
      multiplier: this.multiplier,
      scoreValue: baseScore * this.multiplier,
      comboBreak
    } satisfies RhythmJudgement;

    this.showFloatingText(judgement, wolf);
    return judgement;
  }

  getState() {
    return {
      combo: this.combo,
      multiplier: this.multiplier,
      bestCombo: this.bestCombo,
      perfectPickups: this.perfectPickups,
      goodPickups: this.goodPickups,
      missedPickups: this.missedPickups
    };
  }

  showFloatingLabel(label: string, wolf: { x: number; y: number }, tone: "energy" | "champion" | "warning" = "energy") {
    const color = tone === "champion" ? "#f2f5eb" : tone === "warning" ? "#ffdd66" : "#dfff2f";
    this.createFloatingText(label, color, wolf, tone === "champion" ? 1.1 : 1);
  }

  private getJudgementId(absDelta: number): RhythmJudgementId {
    if (absDelta <= RHYTHM_REWARD_CONFIG.timing.perfectWindow) return "perfect";
    if (absDelta <= RHYTHM_REWARD_CONFIG.timing.goodWindow) return "good";
    if (absDelta <= RHYTHM_REWARD_CONFIG.timing.maxJudgementWindow) return "miss";
    return "miss";
  }

  private getLabel(id: RhythmJudgementId) {
    if (id === "perfect") return "PERFECTO";
    if (id === "good") return "CASI";
    if (id === "miss") return "FUERA";
    return "";
  }

  private showFloatingText(judgement: RhythmJudgement, wolf: { x: number; y: number }) {
    if (judgement.id === "none") return;
    const color = judgement.id === "perfect" ? "#dfff2f" : judgement.id === "good" ? "#b7ff00" : "#ffdd66";
    const suffix = judgement.combo >= 2 && judgement.id !== "miss" ? `  x${judgement.multiplier.toFixed(2).replace(/\\.00$/, "")}` : "";
    const milestone = judgement.combo >= 12 ? "AURA x12" : judgement.combo === 8 ? "AURA x8" : judgement.combo === 4 ? "CADENA x4" : "";
    const label = milestone || `${judgement.label}${suffix}`;
    this.createFloatingText(label, color, wolf, milestone ? 1.12 : 1);
  }

  private createFloatingText(label: string, color: string, wolf: { x: number; y: number }, scaleTarget = 1) {
    const text = this.scene.add
      .text(wolf.x, wolf.y + RHYTHM_REWARD_CONFIG.floatingText.offsetY, label, {
        fontFamily: "Inter, Arial, sans-serif",
        fontSize: RHYTHM_REWARD_CONFIG.floatingText.fontSize,
        fontStyle: "900",
        color,
        stroke: "#050708",
        strokeThickness: 4
      })
      .setOrigin(0.5, 0.5)
      .setDepth(wolf.y + 140)
      .setAlpha(0);
    this.layer.add(text);
    this.floatingTexts.add(text);

    this.scene.tweens.add({
      targets: text,
      y: text.y - RHYTHM_REWARD_CONFIG.floatingText.risePx,
      alpha: { from: 0, to: 1 },
      scale: { from: 0.92, to: 1.04 * scaleTarget },
      duration: 130,
      ease: "Sine.easeOut",
      yoyo: true,
      hold: RHYTHM_REWARD_CONFIG.floatingText.durationMs,
      onComplete: () => {
        this.floatingTexts.delete(text);
        text.destroy();
      }
    });
  }

  private isStrongPickup(pickup: PickupData) {
    return (
      pickup.syncType === "champion_core" ||
      pickup.syncType === "champion_burst" ||
      pickup.syncType === "first_boost_core" ||
      pickup.syncType === "final_champion_core" ||
      pickup.syncType === "pack_echo_core"
    );
  }
}
